// S5 Ticket detail (root 6.7, REQ-8.3): header with reference, title, stage / status and version,
// actions from capabilities and allowed moves only, tabs (Summary, Capture, Reports, Comments,
// History) ~65% and the side panel ~35% (stacked under the main column on phones).
// A ticket the retention rules or an admin removed shows "This ticket was removed on …" (M9).
// A ticket handed to Linear (poc CT-009) shows Linear's status name with the stage underneath and, while it isn't
// finished, "Managed in Linear" in place of the assign and move actions; the page polls while the hand-off is
// pending, so the Linear panel appears when the worker has created the issue.

import type { TicketDetail } from '@feedback/shared/contracts/query';
import type { RemovalNotice } from '@feedback/shared/contracts/retention';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { StageBadge } from '@/components/StageBadge';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { allowedMovesQuery } from '@/features/tickets/dialogs';
import { isManagedInLinear, LinearStatusBadge } from '@/features/tickets/linear';
import { TicketActions } from '@/features/tickets/TicketActions';
import { TicketCapture } from '@/features/tickets/TicketCapture';
import { TicketComments } from '@/features/tickets/TicketComments';
import { TicketHistory } from '@/features/tickets/TicketHistory';
import { ManagedInLinearNotice } from '@/features/tickets/TicketLinearPanel';
import { TicketReports } from '@/features/tickets/TicketReports';
import { TicketSidePanel } from '@/features/tickets/TicketSidePanel';
import { TicketSummary } from '@/features/tickets/TicketSummary';
import { api, isApiError, seg } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cameFromAllTickets, lastQueueSearch } from '@/lib/last-app';
import { requireStaffSession } from '@/lib/session';

const TABS = ['summary', 'capture', 'reports', 'comments', 'history'] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute('/tickets/$ref')({
  // The key is always returned (undefined for Summary or an unknown tab), so a raw `?tab=bogus` can't survive.
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => ({
    tab: TABS.includes(search.tab as Tab) && search.tab !== 'summary' ? (search.tab as Tab) : undefined,
  }),
  beforeLoad: ({ context, location }) => requireStaffSession(context.queryClient, location.href),
  component: TicketPage,
});

/** How often the page looks again while the hand-off to Linear is pending (the worker picks it up in seconds). */
const HANDOFF_POLL_MS = 3000;

const ticketQuery = (ref: string) => ({
  queryKey: ['ticket', ref],
  queryFn: () => api.get<TicketDetail>(`/api/tickets/${seg(ref)}`),
  refetchInterval: (query: { state: { data: TicketDetail | undefined } }) =>
    query.state.data?.linear?.handoff === 'pending' ? HANDOFF_POLL_MS : false,
});

function TicketPage() {
  const { ref } = Route.useParams();
  const ticket = useQuery(ticketQuery(ref));
  const moves = useQuery({ ...allowedMovesQuery(ref), enabled: ticket.isSuccess });
  if (ticket.isPending) {
    return <LoadingState label={`Loading ${ref}`} />;
  }
  if (ticket.isError) {
    return isApiError(ticket.error) && ticket.error.status === 404 ? (
      <TicketNotFound reference={ref} />
    ) : (
      <ErrorState error={ticket.error} onRetry={() => void ticket.refetch()} />
    );
  }
  return <TicketView ticket={ticket.data} moves={moves.data ?? []} />;
}

const BACK_LINK_CLASS = 'inline-flex w-fit items-center gap-1 text-sm text-primary hover:underline';

/** Back to the queue the person came from: All tickets, else the ticket's app queue (CT-001). */
function BackToQueue({ app }: { app: TicketDetail['app'] }) {
  if (cameFromAllTickets()) {
    return (
      <Link to="/queue" search={lastQueueSearch(null)} className={BACK_LINK_CLASS} data-testid="back-to-queue">
        <ArrowLeft className="size-4" aria-hidden="true" />
        All tickets
      </Link>
    );
  }
  return (
    <Link
      to="/apps/$appId/queue"
      params={{ appId: app.id }}
      search={lastQueueSearch(app.id)}
      className={BACK_LINK_CLASS}
      data-testid="back-to-queue"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      {app.name} queue
    </Link>
  );
}

function TicketView({ ticket, moves }: { ticket: TicketDetail; moves: Parameters<typeof TicketActions>[0]['moves'] }) {
  const { tab = 'summary' } = Route.useSearch();
  const navigate = Route.useNavigate();
  const managedLinear = isManagedInLinear(ticket) ? ticket.linear : null;
  return (
    <article className="flex flex-col gap-4">
      <BackToQueue app={ticket.app} />
      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm text-muted">{ticket.reference}</span>
          {managedLinear?.stateName ? (
            <LinearStatusBadge stage={ticket.stage} stateName={managedLinear.stateName} />
          ) : (
            <StageBadge stage={ticket.stage} status={ticket.status} />
          )}
          {ticket.workflowVersion.number !== null ? (
            <Badge variant="muted" title="The workflow version this ticket follows">
              v{ticket.workflowVersion.number}
            </Badge>
          ) : null}
          {ticket.awaitingTriage ? <Badge variant="muted">Awaiting triage</Badge> : null}
        </div>
        <h1 className="text-2xl font-semibold break-words">{ticket.title.text}</h1>
        {managedLinear ? <ManagedInLinearNotice linear={managedLinear} /> : null}
        <TicketActions ticket={ticket} moves={moves} />
      </header>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,65fr)_minmax(0,35fr)]">
        <Tabs
          value={tab}
          onValueChange={(value) =>
            void navigate({ search: value === 'summary' ? {} : { tab: value as Tab }, replace: true })
          }
        >
          <TabsList>
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="capture">Capture</TabsTrigger>
            <TabsTrigger value="reports">Reports ({ticket.reports.length})</TabsTrigger>
            <TabsTrigger value="comments">Comments ({ticket.comments.length})</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          <TabsContent value="summary">
            <TicketSummary ticket={ticket} />
          </TabsContent>
          <TabsContent value="capture">
            <TicketCapture ticket={ticket} />
          </TabsContent>
          <TabsContent value="reports">
            <TicketReports ticket={ticket} />
          </TabsContent>
          <TabsContent value="comments">
            <TicketComments ticket={ticket} />
          </TabsContent>
          <TabsContent value="history">
            <TicketHistory ticket={ticket} />
          </TabsContent>
        </Tabs>
        <TicketSidePanel ticket={ticket} />
      </div>
    </article>
  );
}

/** 404: removed (GET /api/removals/ticket/:ref, M9) or never there / not yours. */
function TicketNotFound({ reference }: { reference: string }) {
  const removal = useQuery({
    queryKey: ['removal', 'ticket', reference],
    queryFn: () => api.get<RemovalNotice>(`/api/removals/ticket/${seg(reference)}`),
    retry: false,
  });
  if (removal.isPending) {
    return <LoadingState rows={1} />;
  }
  if (removal.data?.removed) {
    return (
      <EmptyState icon={Trash2} title={`This ticket was removed on ${formatDateTime(removal.data.performedAt)}`}>
        {reference}{' '}
        {removal.data.action === 'deleted_by_admin'
          ? 'was deleted by a Platform admin.'
          : 'was removed under the retention rules.'}
      </EmptyState>
    );
  }
  return <EmptyState title={`We couldn’t find ${reference}`}>Check the reference.</EmptyState>;
}
